/**
 * `<caos-message-composer>` — where a message is written and sent. (Catalog: Conversation › MessageComposer — the box a message is written in, and the control that sends it.)
 *
 * A growing text box, a send control, an optional attach control and an optional dictate control.
 * It holds what is being typed and reports it; it never sends anything anywhere, never uploads a
 * file and never stores a draft. Each of those is the surrounding application's, and a component
 * that did them would be one that could only be used by the application it was written for.
 *
 * ── The four rules of the box ────────────────────────────────────────────────────────────────
 *   • It GROWS with what is typed, to a stated maximum, and then scrolls. A fixed box makes people
 *     write short messages; an unbounded one pushes the conversation off the screen.
 *   • Enter sends. Shift+Enter starts a line. This is the convention of the genre, and getting it
 *     backwards is the single most complained-about thing a message box can do.
 *   • Sending CLEARS the box, restores its height, and returns focus to it — because the next thing
 *     the person does is almost always write another message.
 *   • A message that is only whitespace is not sent, and the send control says so by being
 *     disabled rather than by refusing after the fact.
 *
 * ── Drafts are reported, not kept ────────────────────────────────────────────────────────────
 * Every keystroke fires `draft`. Whether that is stored, where, and for how long is the surface's
 * decision — per conversation, per person, in a store this library knows nothing about. `value` is
 * how a stored draft comes back in. A composer that wrote to browser storage itself would be
 * choosing a scope for somebody else's data.
 *
 * ── Dictation is offered only where it exists ────────────────────────────────────────────────
 * The dictate control is drawn only when the browser has speech recognition AND the author asked
 * for it. A control that is present and does nothing is worse than an absent one, and this is a
 * capability that genuinely is absent in whole browsers rather than merely disabled. What is
 * recognised is APPENDED to what was already typed, because dictating into a half-written message
 * and losing the half is the failure that makes people stop using it.
 *
 * ── Mentions are OPT-IN, and the box still does not fetch (CAOS-689) ─────────────────────────
 * With `mentions` set, typing `@` opens a picker above the box. The box spots the mention being
 * typed and reports the query through `mention-query`; the surface answers by setting
 * `mentionCandidates`. Nothing is looked up here — who exists and who may be named are questions
 * only the server can answer, and a composer that guessed would offer people who are not there.
 *
 * The box OWNS the picker rather than leaving it to the surface, because the keyboard is the whole
 * point: focus never leaves the textarea, so the arrows, Enter and Escape have to be read here and
 * forwarded. A surface holding its own picker would have to re-implement that, and any surface that
 * got it slightly wrong would send the next typed character nowhere. The picker is still a piece in
 * its own right and can be placed on its own — this is the same arrangement as the stream and the
 * bubbles it draws.
 *
 * `mention-query` carries `null` as well as a string. Null is not "no matches"; it is "nothing is
 * being typed any more", and it is what tells a surface to abandon a lookup that is still in
 * flight. Without it, an answer to a query nobody is typing arrives and opens a picker.
 *
 * Styling seams: `::part(composer)`, `::part(input)`, `::part(actions)`, `::part(send)`,
 * `::part(attach)`, `::part(dictate)`, `::part(mentions)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { CaosMentionPicker, type CaosMentionCandidate } from './mention-picker.js';
import { activeMentionQuery, insertMention } from '../mention-token.js';

const sheet = makeSheet(`
  :host { display: block; }

  .composer {
    display: flex;
    align-items: flex-end;
    gap: var(--caos-space-2);
    padding: var(--caos-space-2) var(--caos-space-3);
    border-top: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface);
    /* The mention picker hangs off this box. Nothing else inside is positioned, so this is inert
       until a picker exists. */
    position: relative;
  }

  /* ABOVE the box, not below it: the composer sits at the bottom of a conversation, so a list
     opening downwards opens off the bottom of the window. */
  caos-mention-picker {
    position: absolute;
    left: var(--caos-space-3);
    right: var(--caos-space-3);
    bottom: 100%;
    margin-bottom: var(--caos-space-1);
    z-index: 1;
  }

  textarea {
    flex: 1 1 auto;
    min-width: 0;
    resize: none;
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    line-height: 1.5;
    padding: var(--caos-space-2);
    overflow-y: auto;
  }
  textarea:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  textarea:disabled { color: var(--caos-color-text-muted); cursor: not-allowed; }

  .actions { display: flex; align-items: center; gap: var(--caos-space-1); }

  button {
    appearance: none;
    border: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text);
    font-family: var(--caos-font);
    font-size: var(--caos-text-label);
    padding: 6px var(--caos-space-2);
    border-radius: var(--caos-radius-sm);
    cursor: pointer;
  }
  button:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  button:disabled { color: var(--caos-color-text-ghost); cursor: not-allowed; }

  .send[data-ready="true"] {
    background: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
    border-color: var(--caos-color-accent-fill);
  }

  .dictate[aria-pressed="true"] {
    background: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
    border-color: var(--caos-color-accent-fill);
  }

  input[type="file"] { display: none; }
`);

/** The default ceiling the box grows to, in pixels, before it starts scrolling instead. */
export const COMPOSER_MAX_HEIGHT = 160;

/** Makes each composer's mention list uniquely addressable — see `renderMentions`. */
let mentionPickerSeq = 0;

/** Does this runtime offer speech recognition at all? */
export function dictationAvailable(view: Window & typeof globalThis = window): boolean {
  return 'SpeechRecognition' in view || 'webkitSpeechRecognition' in view;
}

interface RecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onresult: ((event: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
}

export class CaosMessageComposer extends CaosElement {
  static get observedAttributes(): string[] {
    return ['placeholder', 'disabled', 'send-label', 'attach', 'dictate', 'max-height', 'mentions'];
  }

  private readonly composer: HTMLDivElement;
  private readonly input: HTMLTextAreaElement;
  private readonly send: HTMLButtonElement;
  private readonly attachButton: HTMLButtonElement;
  private readonly fileInput: HTMLInputElement;
  private readonly dictateButton: HTMLButtonElement;
  private recognition: RecognitionLike | null = null;
  /** What was in the box when dictation started, so recognition APPENDS rather than replaces. */
  private beforeDictation = '';
  /** The picker, built only when `mentions` is asked for. Null is the ordinary case. */
  private picker: CaosMentionPicker | null = null;
  /**
   * The last query REPORTED, so `mention-query` fires when the answer changes rather than on every
   * keystroke. A surface that fetched per keystroke over an unchanged query would fetch on the
   * arrow keys.
   *
   * `undefined` means nothing has been reported yet, which is distinct from `null` — "no mention
   * is being typed" is itself a report, and one a surface acts on.
   */
  private reportedQuery: string | null | undefined = undefined;
  /**
   * Whether the surface has answered the question currently being asked.
   *
   * Separate from the candidate list because an EMPTY answer and NO answer are different things to
   * show: "nobody by that name" is useful, and saying it before anybody has looked is wrong. The
   * list alone cannot tell them apart.
   */
  private mentionAnswered = false;
  /**
   * Where a mention the reader DISMISSED with Escape started.
   *
   * Escape has to close the picker without ending the mention — the `@Mor` they typed is still
   * there and still valid, and deleting it would be a surprise. So the mention stays and only the
   * picker is suppressed, until the caret leaves that mention entirely. Re-opening it on the next
   * keystroke would make Escape do nothing at all.
   */
  private mentionDismissedAt: number | null = null;

  constructor() {
    super(sheet);
    this.composer = document.createElement('div');
    this.composer.className = 'composer';
    this.composer.setAttribute('part', 'composer');

    this.input = document.createElement('textarea');
    this.input.setAttribute('part', 'input');
    this.input.rows = 1;
    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (e) => this.onKeydown(e));
    // The caret moves without the text changing — an arrow key, a click, a drag-select — and
    // whether a mention is being typed is a question about the CARET. Watching only `input` leaves
    // the picker open over a mention the reader has already moved away from.
    this.input.addEventListener('keyup', () => this.syncMention());
    this.input.addEventListener('mouseup', () => this.syncMention());
    this.input.addEventListener('blur', () => this.closeMentions());

    const actions = document.createElement('div');
    actions.className = 'actions';
    actions.setAttribute('part', 'actions');

    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.multiple = true;
    this.fileInput.addEventListener('change', () => this.onFiles());

    this.attachButton = document.createElement('button');
    this.attachButton.type = 'button';
    this.attachButton.className = 'attach';
    this.attachButton.setAttribute('part', 'attach');
    this.attachButton.textContent = 'Attach';
    this.attachButton.hidden = true;
    this.attachButton.addEventListener('click', () => this.fileInput.click());

    this.dictateButton = document.createElement('button');
    this.dictateButton.type = 'button';
    this.dictateButton.className = 'dictate';
    this.dictateButton.setAttribute('part', 'dictate');
    this.dictateButton.textContent = 'Dictate';
    this.dictateButton.hidden = true;
    this.dictateButton.setAttribute('aria-pressed', 'false');
    this.dictateButton.addEventListener('click', () => this.toggleDictation());

    this.send = document.createElement('button');
    this.send.type = 'button';
    this.send.className = 'send';
    this.send.setAttribute('part', 'send');
    this.send.textContent = 'Send';
    this.send.addEventListener('click', () => this.submit());

    actions.append(this.attachButton, this.dictateButton, this.send);
    this.composer.append(this.input, actions, this.fileInput);
    this.root.append(this.composer);
  }

  connectedCallback(): void {
    this.render();
  }

  attributeChangedCallback(): void {
    this.render();
  }

  /** What is in the box. Setting it is how a stored draft comes back. */
  get value(): string {
    return this.input.value;
  }
  set value(next: string) {
    this.input.value = next;
    this.grow();
    this.reflectReady();
  }

  /** Nothing can be typed or sent — an outage, a closed conversation, a person without permission. */
  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }
  set disabled(value: boolean) {
    this.toggleAttribute('disabled', value);
  }

  /** Put the cursor in the box. */
  override focus(): void {
    this.input.focus();
  }

  /** Is this box offering mentions at all? */
  get mentionsEnabled(): boolean {
    return this.hasAttribute('mentions');
  }

  /**
   * The answer to the last `mention-query`. Setting it opens the picker over the mention being
   * typed, or says there were no matches; it is ignored when nothing is being typed any more, which
   * is what stops a slow answer from opening a picker after the reader has moved on.
   */
  get mentionCandidates(): CaosMentionCandidate[] {
    return this.picker?.candidates ?? [];
  }
  set mentionCandidates(list: readonly CaosMentionCandidate[]) {
    this.answerMentions(this.reportedQuery ?? '', list);
  }

  /** The query this box is waiting on an answer for, or null when it is not waiting on one. */
  get pendingMentionQuery(): string | null {
    return this.reportedQuery ?? null;
  }

  /**
   * Answer a particular `mention-query`. Returns whether the answer was USED.
   *
   * This is the path a surface doing an asynchronous lookup must take, and the reason is a race the
   * plain setter cannot see. Somebody types `@m`; a lookup starts. They keep typing to `@morgan`;
   * a second lookup starts. If the first answer arrives last — fewer rows, a slower hop, ordinary
   * load — the setter has no way to know it is stale, and shows the people who matched `m` as the
   * answer for `morgan`. The list looks right, and Enter inserts the wrong person.
   *
   * So the answer states which question it answers, and one for a question nobody is asking any
   * more is dropped. Raised by review.
   *
   * The plain `mentionCandidates` setter is kept for a surface that answers synchronously and has
   * no race to lose to. It answers whatever is being asked AT THE MOMENT IT IS CALLED, and does
   * nothing at all when nothing is being asked — so a list set before anybody has typed is not a
   * seed, it is a no-op. Anything asynchronous has to come through here.
   */
  answerMentions(query: string, list: readonly CaosMentionCandidate[]): boolean {
    if (!this.picker) return false;
    if (this.reportedQuery === undefined || this.reportedQuery === null) return false;
    if (query !== this.reportedQuery) return false;
    this.picker.candidates = list;
    // An EMPTY list set here is a real answer — "nobody by that name" — and is shown as one. What
    // must never be shown is the moment before any answer, which is why that is a separate flag
    // rather than `candidates.length === 0`: those two states are indistinguishable from the list
    // alone, and telling somebody there is nobody when nobody has looked yet is a lie the surface
    // cannot correct.
    this.mentionAnswered = true;
    this.picker.hidden = this.activeMention() === null;
    // …and the box has to say the list opened. Without this the highlight moves in an element the
    // reader is not focused on, announced by nothing — the exact failure the combobox wiring
    // exists to prevent, reintroduced one setter away from it.
    this.reflectCombobox();
    return true;
  }

  /** The mention being typed right now, or null. Null while the picker has been dismissed. */
  private activeMention(): { start: number; end: number; query: string } | null {
    if (!this.mentionsEnabled || this.disabled) return null;
    const caret = this.input.selectionStart ?? this.input.value.length;
    // A selection is not a caret: somebody who has selected a range is about to replace it, and
    // inserting a mention at one end of it would be inserting into text they are deleting.
    if ((this.input.selectionEnd ?? caret) !== caret) return null;
    const active = activeMentionQuery(this.input.value, caret);
    if (!active) return null;
    if (this.mentionDismissedAt === active.start) return null;
    return active;
  }

  /** True only for the duration of a send dispatch. See submit(). */
  private sending = false;

  /**
   * Send what is in the box, if anything is. Returns what was sent, or null.
   *
   * A SECOND CLICK CANNOT SEND THE SAME TEXT TWICE, and it is worth saying which mechanism does
   * that, because the obvious answer is the wrong one. It is not `disabled`: a surface sets that
   * when its own round trip finishes, which is far too late to help. It is that the box is
   * cleared on the line after the dispatch, synchronously, so the second click finds it empty and
   * returns null before dispatching anything.
   *
   * That leaves one way in, and `sending` closes it. `dispatchEvent` runs its listeners BEFORE it
   * returns, so for the duration of the send handler the box still holds the text - and a surface
   * that calls back into the composer from that handler, to send a queued line or to re-focus
   * through a path that ends in submit(), would re-enter here and dispatch the same body again.
   * Nothing in this package does that; a surface built on it can, and would have no way of
   * knowing it had.
   */
  submit(): string | null {
    if (this.disabled || this.sending) return null;
    const body = this.input.value.trim();
    if (body === '') return null;
    this.sending = true;
    try {
      this.dispatchEvent(new CustomEvent('send', { bubbles: true, composed: true, detail: { body } }));
    } finally {
      this.sending = false;
    }
    this.input.value = '';
    this.grow();
    this.reflectReady();
    this.dispatchEvent(new CustomEvent('draft', { bubbles: true, composed: true, detail: { value: '' } }));
    // The box is empty, so whatever was half-typed is gone with it.
    this.mentionDismissedAt = null;
    this.closeMentions();
    // Back to the box: the next thing somebody does after sending a message is write another one.
    this.input.focus();
    return body;
  }

  private onInput(): void {
    this.grow();
    this.reflectReady();
    this.dispatchEvent(new CustomEvent('draft', { bubbles: true, composed: true, detail: { value: this.input.value } }));
    this.syncMention();
  }

  /**
   * Report the mention being typed, if that answer has changed, and keep the picker in step.
   *
   * The picker is hidden rather than emptied when nothing is being typed: its candidates are the
   * answer to a query that was asked, and clearing them here would race the answer still arriving.
   */
  private syncMention(): void {
    if (!this.picker) return;
    const active = this.activeMention();

    // Left the mention we dismissed — Escape's suppression was about THAT mention, not about
    // mentions in general.
    if (this.mentionDismissedAt !== null) {
      const raw = activeMentionQuery(this.input.value, this.input.selectionStart ?? 0);
      if (!raw || raw.start !== this.mentionDismissedAt) this.mentionDismissedAt = null;
    }

    const query = active ? active.query : null;
    const changed = query !== this.reportedQuery;
    // A new question has no answer yet. Leaving the previous answer on screen would offer the
    // people who matched what was typed a keystroke ago against what is being typed now — which
    // looks like a working picker right up until somebody presses Enter on it.
    if (changed) this.mentionAnswered = false;
    this.picker.hidden = active === null || !this.mentionAnswered;
    this.reflectCombobox();
    if (!changed) return;
    this.reportedQuery = query;
    this.dispatchEvent(new CustomEvent('mention-query', { bubbles: true, composed: true, detail: { query } }));
  }

  /** Stop offering, and say so, without touching what was typed. */
  private closeMentions(): void {
    if (!this.picker) return;
    this.picker.hidden = true;
    this.mentionAnswered = false;
    this.reflectCombobox();
    if (this.reportedQuery === null) return;
    this.reportedQuery = null;
    this.dispatchEvent(new CustomEvent('mention-query', { bubbles: true, composed: true, detail: { query: null } }));
  }

  /**
   * Write the chosen candidate into the box in place of the mention being typed.
   *
   * Returns whether anything was written, so the key handler can tell "Enter chose somebody" from
   * "Enter should send the message" — the same distinction the picker's own `chooseActive` makes.
   */
  private takeMention(choice: CaosMentionCandidate): boolean {
    const caret = this.input.selectionStart ?? this.input.value.length;
    const next = insertMention(this.input.value, caret, choice);
    if (next.text === this.input.value) return false;
    this.input.value = next.text;
    this.input.setSelectionRange(next.caret, next.caret);
    this.grow();
    this.reflectReady();
    this.dispatchEvent(new CustomEvent('draft', { bubbles: true, composed: true, detail: { value: this.input.value } }));
    // Raised as well as `draft`, because a surface wants the CHOICE and not only the new text: it
    // is what lets one keep the labels it has already resolved instead of asking again.
    this.dispatchEvent(new CustomEvent('mention', { bubbles: true, composed: true, detail: { ...choice } }));
    this.closeMentions();
    this.input.focus();
    return true;
  }

  /**
   * Say, on the TEXTAREA, that a list is open beside it and which row is highlighted.
   *
   * This is the half of the combobox pattern that lives on the focused element. Without it the
   * highlight moves in an element the reader is not focused on and is therefore never announced —
   * the list would be visible and, to a screen reader, silent.
   */
  private reflectCombobox(): void {
    if (!this.picker || !this.mentionsEnabled) {
      this.input.removeAttribute('role');
      this.input.removeAttribute('aria-expanded');
      this.input.removeAttribute('aria-controls');
      this.input.removeAttribute('aria-activedescendant');
      this.input.removeAttribute('aria-autocomplete');
      return;
    }
    const open = !this.picker.hidden;
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-expanded', String(open));
    this.input.setAttribute('aria-controls', this.picker.id);
    const active = open ? this.picker.activeOptionId : null;
    if (active) this.input.setAttribute('aria-activedescendant', active);
    else this.input.removeAttribute('aria-activedescendant');
  }

  private onKeydown(event: KeyboardEvent): void {
    if (this.onMentionKeydown(event)) return;
    if (event.key !== 'Enter') return;
    // Any modifier means "a line, not a send" — including the composing key on a keyboard where
    // that is how a line is started. Only a bare Enter sends.
    if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return;
    // Mid-composition — an IME is assembling a character and Enter is choosing it, not sending.
    if (event.isComposing) return;
    event.preventDefault();
    this.submit();
  }

  /**
   * The picker's keyboard, read here because focus is here. Returns whether the key was spent on
   * the picker, in which case the box's own handling of it does not run.
   *
   * Only while the picker is actually SHOWING: with no list open, the arrows move the caret and
   * Enter sends, which is what every one of these keys does in a box with no mentions in it.
   */
  private onMentionKeydown(event: KeyboardEvent): boolean {
    const picker = this.picker;
    if (!picker || picker.hidden) return false;
    // A modifier means the reader is doing something else with the key — selecting to the end of
    // the line, starting a new line — and the picker does not get to intercept that.
    if (event.altKey || event.ctrlKey || event.metaKey) return false;

    if (event.key === 'Escape') {
      event.preventDefault();
      const raw = activeMentionQuery(this.input.value, this.input.selectionStart ?? 0);
      this.mentionDismissedAt = raw ? raw.start : null;
      this.closeMentions();
      return true;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      picker.moveDown();
      this.reflectCombobox();
      return true;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      picker.moveUp();
      this.reflectCombobox();
      return true;
    }
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      const choice = picker.active;
      if (!choice) return false;
      event.preventDefault();
      this.takeMention(choice);
      return true;
    }
    return false;
  }

  private onFiles(): void {
    const files = this.fileInput.files;
    if (!files || files.length === 0) return;
    this.dispatchEvent(
      new CustomEvent('attach', { bubbles: true, composed: true, detail: { files: Array.from(files) } }),
    );
    // Cleared so that choosing the SAME file twice in a row still reports: a file input fires
    // `change` only when the value differs, and the second attempt would be silent.
    this.fileInput.value = '';
  }

  private grow(): void {
    const max = Number(this.getAttribute('max-height') ?? COMPOSER_MAX_HEIGHT);
    const ceiling = Number.isFinite(max) && max > 0 ? max : COMPOSER_MAX_HEIGHT;
    // Collapse first: `scrollHeight` of a box already tall enough for its content reports the
    // box, not the content, so a box that has grown never shrinks again.
    this.input.style.height = '0px';
    this.input.style.height = `${Math.min(this.input.scrollHeight, ceiling)}px`;
  }

  private reflectReady(): void {
    const ready = !this.disabled && this.input.value.trim() !== '';
    this.send.disabled = !ready;
    this.send.dataset['ready'] = String(ready);
  }

  private render(): void {
    this.input.placeholder = this.getAttribute('placeholder') ?? 'Write a message…';
    this.input.disabled = this.disabled;
    this.send.textContent = this.getAttribute('send-label') ?? 'Send';
    this.attachButton.hidden = !this.hasAttribute('attach');
    this.attachButton.disabled = this.disabled;
    // Offered only where it exists AND where it was asked for. A control that is present and does
    // nothing is worse than an absent one.
    this.dictateButton.hidden = !this.hasAttribute('dictate') || !dictationAvailable();
    this.dictateButton.disabled = this.disabled;
    this.renderMentions();
    this.grow();
    this.reflectReady();
  }

  /** Build the picker the first time mentions are asked for; take it away when they are not. */
  private renderMentions(): void {
    if (!this.mentionsEnabled) {
      if (this.picker) {
        this.picker.remove();
        this.picker = null;
        this.reportedQuery = undefined;
        this.mentionAnswered = false;
      }
      this.reflectCombobox();
      return;
    }
    if (!this.picker) {
      const picker = new CaosMentionPicker();
      picker.setAttribute('part', 'mentions');
      // Named so `aria-controls` on the textarea has something to point at. Unique per instance,
      // because two composers on one page would otherwise both claim the same id and the second
      // one's textarea would point at the first one's list.
      picker.id = `caos-composer-mentions-${++mentionPickerSeq}`;
      picker.hidden = true;
      picker.addEventListener('choose', (event) => {
        this.takeMention((event as CustomEvent<CaosMentionCandidate>).detail);
      });
      picker.addEventListener('active-change', () => this.reflectCombobox());
      this.composer.append(picker);
      this.picker = picker;
    }
    this.reflectCombobox();
  }

  private toggleDictation(): void {
    if (this.recognition) {
      this.recognition.stop();
      return;
    }
    const view = window as unknown as Record<string, new () => RecognitionLike>;
    const Ctor = view['SpeechRecognition'] ?? view['webkitSpeechRecognition'];
    if (!Ctor) return;
    const recognition = new Ctor();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = this.ownerDocument.documentElement.lang || 'en-US';
    this.beforeDictation = this.input.value;
    // Each result REPLACES what is in the box with "what was there when dictation started" plus
    // what has been heard so far, because interim results revise themselves. A consequence worth
    // stating: typing while dictating is not supported — the next interim result overwrites it.
    // Dictating into a half-written message and keeping the half is what this is for; editing that
    // half at the same time is a different feature, and doing it badly is worse than not offering it.
    recognition.onresult = (event): void => {
      let heard = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        heard += event.results[i]?.[0]?.transcript ?? '';
      }
      if (heard === '') return;
      const joiner = this.beforeDictation && !this.beforeDictation.endsWith(' ') ? ' ' : '';
      this.input.value = `${this.beforeDictation}${joiner}${heard}`;
      this.onInput();
    };
    const finish = (): void => {
      this.recognition = null;
      this.dictateButton.setAttribute('aria-pressed', 'false');
    };
    recognition.onend = finish;
    recognition.onerror = finish;
    this.recognition = recognition;
    this.dictateButton.setAttribute('aria-pressed', 'true');
    recognition.start();
  }

  disconnectedCallback(): void {
    // A recognition session outlives the element that started it unless it is stopped: the box is
    // gone, the microphone is not, and nothing is left on screen to turn it off.
    this.recognition?.stop();
    this.recognition = null;
  }
}

define('caos-message-composer', CaosMessageComposer);

declare global {
  interface HTMLElementTagNameMap {
    'caos-message-composer': CaosMessageComposer;
  }
}
